import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabaseClient';

const AnalyticsModal = ({ isOpen, onClose, service }) => {
  if (!isOpen || !service) return null;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [analytics, setAnalytics] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    monthlyTrend: [],
  });

  const last3Months = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 2; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({
        key,
        label: `${d.getMonth() + 1}월`,
      });
    }
    return months;
  }, []);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const serviceType = service?.name;
        if (!serviceType) {
          throw new Error('서비스 정보를 불러오지 못했습니다.');
        }

        const { data, error } = await supabase
          .from('order_items')
          .select('order_id, price, orders ( order_date )')
          .eq('service_type', serviceType);

        if (error) throw error;

        const rows = data || [];
        const orderIds = new Set();
        let totalRevenue = 0;

        const monthMap = new Map();
        last3Months.forEach((m) => {
          monthMap.set(m.key, { orderIds: new Set(), revenue: 0, label: m.label });
        });

        rows.forEach((r) => {
          if (r?.order_id != null) {
            orderIds.add(r.order_id);
          }
          totalRevenue += Number(r?.price || 0);

          const orderDate = r?.orders?.order_date;
          if (!orderDate) return;
          const d = new Date(orderDate);
          if (Number.isNaN(d.getTime())) return;
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

          const bucket = monthMap.get(key);
          if (!bucket) return;
          if (r?.order_id != null) {
            bucket.orderIds.add(r.order_id);
          }
          bucket.revenue += Number(r?.price || 0);
        });

        const totalOrders = orderIds.size;
        const averageOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;

        const monthlyTrend = last3Months.map((m) => {
          const bucket = monthMap.get(m.key);
          return {
            month: m.label,
            orders: bucket ? bucket.orderIds.size : 0,
            revenue: bucket ? bucket.revenue : 0,
          };
        });

        if (!mounted) return;
        setAnalytics({
          totalOrders,
          totalRevenue,
          averageOrderValue,
          monthlyTrend,
        });
      } catch (e) {
        if (!mounted) return;
        setAnalytics({
          totalOrders: 0,
          totalRevenue: 0,
          averageOrderValue: 0,
          monthlyTrend: last3Months.map((m) => ({ month: m.label, orders: 0, revenue: 0 })),
        });
        setErrorMessage(e?.message || '분석 데이터를 불러오지 못했습니다.');
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [isOpen, last3Months, service]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ko-KR')?.format(price);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-card shadow-lg">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">{service?.name} 분석</h2>
            <p className="text-sm text-muted-foreground">서비스 성과 및 통계</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
            aria-label="닫기"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {errorMessage && (
            <div className="rounded-lg bg-error/10 p-4">
              <p className="text-sm text-error">{errorMessage}</p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">총 주문 수</p>
                <Icon name="ShoppingCart" size={20} className="text-accent" />
              </div>
              <p className="text-2xl font-bold text-foreground">
                {isLoading ? '-' : analytics?.totalOrders}
              </p>
            </div>

            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">총 매출</p>
                <Icon name="DollarSign" size={20} className="text-success" />
              </div>
              <p className="text-2xl font-bold text-foreground">
                {isLoading ? '-' : `${formatPrice(analytics?.totalRevenue)}원`}
              </p>
            </div>

            <div className="rounded-lg bg-muted/30 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">평균 주문 금액</p>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {isLoading ? '-' : `${formatPrice(analytics?.averageOrderValue)}원`}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="mb-4 text-lg font-bold text-foreground">월별 추이</h3>
            <div className="space-y-4">
              {analytics?.monthlyTrend?.map((data, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="mb-1 text-sm font-medium text-foreground">{data?.month}</p>
                    <div className="h-2 w-full rounded-full bg-muted">
                      <div
                        className="h-2 rounded-full bg-primary"
                        style={{
                          width: `${(
                            data?.orders /
                            Math.max(
                              1,
                              ...analytics?.monthlyTrend?.map((d) => d?.orders)
                            )
                          ) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-sm font-bold text-foreground">{data?.orders}건</p>
                    <p className="text-xs text-muted-foreground">
                      {formatPrice(data?.revenue)}원
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal;